import { useEffect, useState } from "react";
import moment from "moment";
import { FiArrowDown, FiArrowUp, FiCheck, FiChevronRight, FiDownload,FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { CURRENCY_SYMBOL, PAGE_TYPE, STATUS_CODES, WHATSAPP_WEB_URL } from "@src/config/constant";
import FullPageLoader from "@src/common/FullPageLoader";
import { TravelService } from "@src/services/TravelService";
import FeatureIncludedPopup, { FeatureIncludedGroup } from "./popups/FeatureIncludedPopup";


const ArrowDownIcon = FiArrowDown as any;
const ArrowUpIcon = FiArrowUp as any;
const CheckIcon = FiCheck as any;
const ChevronRightIcon = FiChevronRight as any;
const DownloadIcon = FiDownload as any;
const CloseIcon = FiX as any;

export type TravelPlan = {
    id: string;
    planId?: string | number;
    insurerId?: string | number;
    insurer: string;
    tier: string;
    price: string;
    logoText: string;
    logoUrl?: string;
    commission: string;
    tag?: string;
    benefits: string[];
    featureGroups?: FeatureIncludedGroup[];
};

type PlanSelectionProps = {
    selectedPlanIds: string[];
    onTogglePlan: (plan: TravelPlan) => void;
    travellerList?: any[];
    travellerDobValues?: any[];
    buildCreateLeadPayload?: (travellerList: any[]) => any;
    setLoading: (loading: boolean) => void;
    leadId?: number | null;
    setLeadId?: (leadId: number | null) => void;
};

const getDateValue = (value: any, format = "YYYY-MM-DD") => (
    value ? moment(value).format(format) : ""
);

const buildInsuredPayload = (dobValues: any[] = [], payload: any = {}) => (
    dobValues
        .map((dobValue: any) => getDateValue(dobValue))
        .map((dob: string, index: number) => {
            if (!dob) return null;

            const existingInsured = payload?.insured?.[index] || {};
            const fallbackFirstName = index === 0 ? getTextValue(payload?.primary_first_name) : "";

            return {
                ...existingInsured,
                dob,
                first_name: getTextValue(existingInsured?.first_name) || fallbackFirstName,
            };
        })
        .filter(Boolean)
);

type TravelPlanSortBy = "ASC" | "DSC";

export const getTextValue = (value: any) => (value === undefined || value === null ? "" : String(value).trim());

const formatAmount = (value: any) => {
    const textValue = getTextValue(value);
    if (!textValue) return "";

    const numericValue = Number(textValue);
    if (Number.isFinite(numericValue)) {
        return new Intl.NumberFormat("en-PH", { maximumFractionDigits: 2 }).format(numericValue);
    }

    return textValue;
};

const formatCurrency = (value: any) => {
    const formattedAmount = formatAmount(value);
    return formattedAmount ? `${CURRENCY_SYMBOL} ${formattedAmount}` : "";
};

const isActiveApiItem = (item: any) => item?.status === undefined || Number(item.status) === 1;

const getBenefitGroupItems = (group: any) => {
    if (Array.isArray(group?.item)) return group.item;
    if (Array.isArray(group?.items)) return group.items;
    return [];
};

const getBenefitItems = (benefitList: any[] = []) => (
    benefitList
        .filter(isActiveApiItem)
        .flatMap(getBenefitGroupItems)
        .filter((item: any) => isActiveApiItem(item) && getTextValue(item?.name))
);

const getBenefitAmount = (item: any) => getTextValue(item?.offered_lebel || item?.offered_label || item?.amount);

const getBenefitDisplayText = (item: any) => {
    const title = getTextValue(item?.name);
    const amount = getBenefitAmount(item);
    return amount ? `${title} ${amount}` : title;
};

const normalizeBenefitGroups = (benefitList: any[] = []): FeatureIncludedGroup[] => (
    benefitList
        .filter(isActiveApiItem)
        .map((group: any) => {
            const items = getBenefitGroupItems(group)
                .filter((item: any) => isActiveApiItem(item) && getTextValue(item?.name))
                .map((item: any) => ({
                    title: getTextValue(item.name),
                    amount: getBenefitAmount(item),
                }));

            return {
                title: getTextValue(group?.name) || "Benefits",
                items,
            };
        })
        .filter((group: FeatureIncludedGroup) => group.items.length)
);

const getVisibleBenefits = (benefitList: any[] = []) => {
    const benefitItems = getBenefitItems(benefitList);
    const highlightedItems = benefitItems.filter((item: any) => Number(item?.is_display) === 1);
    const remainingItems = benefitItems.filter((item: any) => Number(item?.is_display) !== 1);

    return [...highlightedItems, ...remainingItems]
        .slice(0, 5)
        .map(getBenefitDisplayText)
        .filter(Boolean);
};

const getPlanLogoText = (insurerName: string) => {
    const initials = insurerName
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0])
        .join("")
        .slice(0, 3);

    return initials || insurerName.slice(0, 4) || "plan";
};

const getApiPlanId = (plan: any, index: number) => {
    const directId = plan?.id ?? plan?.quotation_plan_id ?? plan?.quotation_id;
    if (directId !== undefined && directId !== null && directId !== "") return String(directId);

    const idParts = [plan?.insurer_id, plan?.trip_id, plan?.plan_id]
        .filter((value) => value !== undefined && value !== null && value !== "");

    return idParts.length ? idParts.join("-") : `travel-plan-${index + 1}`;
};

export const getPlanListFromResponse = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];

    const possibleList = [
        payload.plan_list,
        payload.planList,
        payload.plans,
        payload.list,
        payload.rows,
        payload.result,
    ].find(Array.isArray);

    if (possibleList) return possibleList;

    if (payload.data && payload.data !== payload) {
        const nestedPlanList = getPlanListFromResponse(payload.data);
        if (nestedPlanList.length) return nestedPlanList;
    }

    return payload.plan_id || payload.benefit_list ? [payload] : [];
};

export const normalizeTravelPlans = (planList: any[] = []): TravelPlan[] => (
    planList.map((plan: any, index: number) => {
        const benefitList = Array.isArray(plan?.benefit_list) ? plan.benefit_list : [];
        const insurerName = getTextValue(plan?.insurer_name);
        const planName = getTextValue(plan?.plan_name);
        const premium = plan?.gross_premium ?? plan?.net_premium;
        const commission = plan?.agent_commission;

        return {
            id: getApiPlanId(plan, index),
            planId: plan?.plan_id,
            insurerId: plan?.insurer_id,
            insurer: insurerName,
            tier: planName,
            price: formatCurrency(premium),
            logoText: getPlanLogoText(insurerName),
            logoUrl: getTextValue(plan?.insurer_logo),
            commission: commission !== undefined && commission !== null
                ? `Your commission would be ${formatCurrency(commission)}`
                : "",
            tag: plan?.is_best_deal ? "Best Deal" : undefined,
            benefits: getVisibleBenefits(benefitList),
            featureGroups: normalizeBenefitGroups(benefitList),
        };
    }).filter((plan: TravelPlan) => plan.insurer || plan.tier)
);

type TravelPlanCardProps = {
    plan: TravelPlan;
    isSelected?: boolean;
    onToggle?: () => void;
    actionLabel?: string;
    className?: string;
    showSelectionControl?: boolean;
    onSeeAll?: (plan: TravelPlan) => void;
};

export function TravelPlanCard({
    plan,
    isSelected = false,
    onToggle,
    actionLabel = "Select Plan",
    className = "",
    showSelectionControl = false,
    onSeeAll,
}: TravelPlanCardProps) {
    const visibleBenefits = plan.benefits.slice(0, 5);
    const inputId = `travel-plan-${plan.id}`;

    return (
        <article className={`travel-plan-card${isSelected ? " active" : ""}${plan.tag ? " has-plan-tag" : ""}${className ? ` ${className}` : ""}`}>
            {plan.tag ? <div className="travel-plan-tag f-12">{plan.tag}</div> : null}

            <div className="travel-plan-card-head">
                <div className="travel-plan-provider">
                    <div className="travel-plan-logo">
                        {plan.logoUrl ? (
                            <img src={plan.logoUrl} alt={plan.insurer} />
                        ) : (
                            <>
                                <strong>{plan.logoText}</strong>
                                <span className="f-10">insurance</span>
                            </>
                        )}
                    </div>
                    <div>
                        <h3 className="f-16">{plan.insurer}</h3>
                        <p className="f-15">{plan.tier}</p>
                    </div>
                </div>

                <div className="travel-plan-price f-16">{plan.price}</div>

                <button type="button" className="btn-primary travel-plan-select-btn" onClick={onToggle}>
                    {actionLabel}
                </button>

                {showSelectionControl ? (
                    <div className="custom-control custom-checkbox travel-plan-check">
                        <input
                            checked={isSelected}
                            id={inputId}
                            type="checkbox"
                            className="custom-control-input"
                            name="travel_plan_select"
                            onChange={onToggle}
                        />
                        <label className="custom-control-label f-11" htmlFor={inputId}>Select</label>
                    </div>
                ) : null}
            </div>

            <div className="travel-plan-benefits">
                {visibleBenefits.map((benefit) => (
                    <div className="travel-plan-benefit f-13" key={`${plan.id}-${benefit}`}>
                        <CheckIcon />
                        <span>{benefit}</span>
                    </div>
                ))}
                <button type="button" className="travel-plan-see-all f-13 pointer" onClick={() => onSeeAll?.(plan)}>
                    See All <ChevronRightIcon />
                </button>
            </div>

            <div className="travel-plan-commission f-11">{plan.commission}</div>
        </article>
    );
}

export default function PlanSelection({ selectedPlanIds, onTogglePlan, travellerList, travellerDobValues = [], buildCreateLeadPayload, setLoading, leadId, setLeadId }: PlanSelectionProps) {
    const [plans, setPlans] = useState<TravelPlan[]>([]);
    const [isPlanListLoading, setIsPlanListLoading] = useState(true);
    const [sortBy, setSortBy] = useState<TravelPlanSortBy>("ASC");
    const [selectedFeaturePlan, setSelectedFeaturePlan] = useState<TravelPlan | null>(null);
    const selectedPlansCount = plans.filter((plan) => selectedPlanIds.includes(plan.id)).length;
    const [showSelectedPlansPopup, setShowSelectedPlansPopup] = useState<boolean>(selectedPlansCount >= 1);
    const selectedFeatureGroups = selectedFeaturePlan?.featureGroups?.length
        ? selectedFeaturePlan.featureGroups
        : [{
            title: "Features Included",
            items: selectedFeaturePlan?.benefits.map((benefit) => ({ title: benefit })) || [],
        }];

    const clearSelectedPlans = () => {
       setShowSelectedPlansPopup(false);
    };

    const handleShare = async (type: "whatsapp" | "email" | "download") => {
        if (typeof buildCreateLeadPayload !== "function") {
            // eslint-disable-next-line no-console
            console.warn("buildCreateLeadPayload is not provided to PlanSelection");
            return;
        }

        try {
            setLoading(true);
            const payload = buildCreateLeadPayload(Array.isArray(travellerList) ? travellerList : []);
            // eslint-disable-next-line no-console
            if (leadId) payload.lead_id = leadId;
            payload.page_type = PAGE_TYPE.PLAN_SELECTION;

            // const selectedInsurerId = selectedPlanIds
            //     .map((selectedId) => plans.find((plan) => plan.id === selectedId)?.insurerId)
            //     .find((value) => value !== undefined && value !== null && value !== "");
            // if (selectedInsurerId !== undefined && selectedInsurerId !== null && selectedInsurerId !== "") {
            //     payload.insurer_id = selectedInsurerId;
            // }

            const selectedNumericPlanIds = selectedPlanIds
                .map((selectedId) => plans.find((plan) => plan.id === selectedId)?.planId ?? selectedId)
                .map((planId) => {
                    const numericValue = Number(planId);
                    return Number.isFinite(numericValue) ? numericValue : null;
                })
                .filter((planId): planId is number => planId !== null);

            if (selectedNumericPlanIds.length) {
                payload.plan_id = selectedNumericPlanIds[selectedNumericPlanIds.length - 1];
                payload.shared_plan_ids = selectedNumericPlanIds.map(String).join(",");
            }
            const insured = travellerDobValues
                .map((dobValue: any) => getDateValue(dobValue))
                .filter(Boolean)
                .map((dob: string, index: number) => ({
                    dob,
                    first_name: index === 0 ? getTextValue(payload.primary_first_name) : "",
                }));

            payload.insured = insured;    
            delete payload?.primary_first_name;

            const response: any = await TravelService.createTravelLead(payload);
            // eslint-disable-next-line no-console
            
            if(response?.data?.data?.id){
                setLeadId?.(response?.data?.data?.id);
            }
            
            const pdfPath = response?.data?.data?.pdf_path || response?.data?.pdf_path || response?.pdf_path || "";
            if (!pdfPath) {
                toast.error("PDF path not found in response");
                return;
            }

            if (type === "download") {
                window.open(pdfPath, "_blank");
                return;
            }

            const shareText = `Travel quotation: ${pdfPath}`;
            if (type === "whatsapp") {
                window.open(`${WHATSAPP_WEB_URL}${encodeURIComponent(shareText)}`, "_blank");
                return;
            }

            window.open(`mailto:?subject=${encodeURIComponent("Travel quotation")}&body=${encodeURIComponent(shareText)}`, "_blank");
        } catch (error: any) {
            toast.error(error?.message || "Unable to share quotation");
        } finally {
            setLoading(false);
        }
    };

    const handleSortToggle = () => {
        setSortBy((currentSort) => currentSort === "ASC" ? "DSC" : "ASC");
    };

    useEffect(() => {
        let isMounted = true;

        const fetchTravelPlans = async () => {
            setIsPlanListLoading(true);

            if (typeof buildCreateLeadPayload !== "function") {
                // eslint-disable-next-line no-console
                console.warn("buildCreateLeadPayload is not provided to PlanSelection");
                return;
            }

            const {traveller_count,trip_start_date,trip_end_date, insurance_type, trip_type, origin_country_id, dest_country_id} = buildCreateLeadPayload(Array.isArray(travellerList) ? travellerList : []);
            const insured = travellerDobValues
                .map((dobValue: any) => getDateValue(dobValue))
                .filter(Boolean)
                .map((dob: string) => ({ dob }));

            try {
                const response: any = await TravelService.travelPlanList({
                    sort_by: sortBy,
                    traveller_count,
                    trip_start_date,
                    trip_end_date,
                    insurance_type,
                    trip_type,
                    origin_country_id,
                    dest_country_id,
                    insured
                });
                const responseData = response?.data;
                const apiPlanList = getPlanListFromResponse(responseData?.data ?? responseData);
                const isSuccessResponse = responseData?.status === STATUS_CODES.success || response?.status === STATUS_CODES.success;

                if (isMounted && (isSuccessResponse || apiPlanList.length)) {
                    setPlans(normalizeTravelPlans(apiPlanList));
                    return;
                }

                const errorMessage = getTextValue(responseData?.message);
                if (errorMessage) toast.error(errorMessage);
            } catch (error: any) {
                toast.error(error?.message || "Unable to fetch travel plans");
            } finally {
                if (isMounted) setIsPlanListLoading(false);
            }
        };

        fetchTravelPlans();

        return () => {
            isMounted = false;
        };
    }, [sortBy]);

    useEffect(() => {
        if (isPlanListLoading) return;

        const apiPlanIds = new Set(plans.map((plan) => plan.id));
        const selectedApiPlan = plans.find((plan) => selectedPlanIds.includes(plan.id));
        const selectedPlanIdValue = selectedPlanIds
            .map((selectedId) => selectedId.replace("edit-plan-", ""))
            .find((selectedId) => selectedId);
        const matchingPlanByPlanId = selectedPlanIdValue
            ? plans.find((plan) => String(plan.planId) === String(selectedPlanIdValue))
            : undefined;
        const staleSelectedEditPlans = selectedPlanIds
            .filter((selectedId) => selectedId.startsWith("edit-plan-") && !apiPlanIds.has(selectedId))
            .map((selectedId) => ({ id: selectedId } as TravelPlan));
        const hasSelectedApiPlan = Boolean(selectedApiPlan);

        if (!hasSelectedApiPlan && matchingPlanByPlanId) {
            staleSelectedEditPlans.forEach((plan) => onTogglePlan(plan));
            onTogglePlan(matchingPlanByPlanId);
        }
    }, [isPlanListLoading, onTogglePlan, plans, selectedPlanIds]);

    useEffect(() => {
        if(selectedPlansCount>=1){
            setShowSelectedPlansPopup(true);
        }else{
            setShowSelectedPlansPopup(false);
        }
    },[selectedPlansCount])

    return (
        <div className="travel-plan-selection">
            <div className="travel-plan-selection-head">
                <h2 className="heading-h2">Travel Plan Selection</h2>
                <div className="travel-plan-actions">
                    <button
                        type="button"
                        className="f-13"
                        onClick={handleSortToggle}
                        disabled={isPlanListLoading}
                        aria-label={`Sort plans ${sortBy === "ASC" ? "descending" : "ascending"}`}
                    >
                        Sort by <span>{sortBy === "ASC" ? <ArrowUpIcon /> : <ArrowDownIcon />}</span>
                    </button>
                    <button
                        type="button"
                        className="f-13"
                        onClick={() => handleShare("download")}
                        disabled={!selectedPlansCount}
                    >
                        Download <DownloadIcon />
                    </button>
                </div>
            </div>

            <div className="travel-plan-list">
                {isPlanListLoading ? (
                    <FullPageLoader show={isPlanListLoading} />
                ) : plans.length ? (
                    plans.map((plan) => (
                        <TravelPlanCard
                            key={plan.id}
                            plan={plan}
                            isSelected={selectedPlanIds.includes(plan.id)}
                            onToggle={() => onTogglePlan(plan)}
                            onSeeAll={setSelectedFeaturePlan}
                            showSelectionControl
                        />
                    ))
                ) : (
                    <div className="travel-plan-empty f-13">No plans available</div>
                )}
            </div>

            <FeatureIncludedPopup
                isOpen={Boolean(selectedFeaturePlan)}
                onClose={() => setSelectedFeaturePlan(null)}
                title="Features Included"
                features={selectedFeatureGroups}
            />

            {showSelectedPlansPopup ? (
                <div className="travel-selected-plans-popup" role="status">
                    <p className="f-15">{selectedPlansCount} Quotation Plan Selected</p>
                    <div className="travel-selected-plans-actions">
                        <button
                            type="button"
                            className="travel-selected-plans-close"
                            aria-label="Clear selected quotation plans"
                            onClick={clearSelectedPlans}
                        >
                            <CloseIcon />
                        </button>
                        <button
                            type="button"
                            className="travel-selected-plans-mail"
                            aria-label="Share selected plans by email"
                            onClick={() => handleShare("email")}
                            // disabled={isShareLoading}
                        >
                            <i className="ic-email"></i>
                        </button>
                        <button
                            type="button"
                            className="travel-selected-plans-whatsapp f-13"
                            onClick={() => handleShare("whatsapp")}
                            // disabled={isShareLoading}
                        >
                            <i className="ic-whatsapp"></i>
                            Share On WhatsApp
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
